import _ from 'lodash';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import Color from 'packages/kakapo-ui-kit/components/common/Color';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import DashboardDepartmentsSelectorModal from './DashboardDepartmentsSelectorModal';

const Link = styled(Box)`
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;
	cursor: pointer;
`;

export default function DashboardDepartmentsSelector(props: {
	organization?: boolean;
	engagement?: boolean;
}) {
	const { t } = useTranslation();

	const [{ value }, , { setValue }] = useField<string[]>('groupIDs');

	const query = graphql.useDepartmentsWithParentsQuery({
		variables: {
			filter: {
				isArchived: false,
			},
			first: 1000,
		},
	});

	const groups = query.data?.groups.edges
		.map(({ node }) => node)
		.filter((node) => value.includes(node.id));

	const [modal, setModal] = useState(false);

	useEffect(() => {
		if (props.organization) {
			return;
		}

		if (value.length > 0) {
			return;
		}

		setValue(
			query.data?.groups.edges
				.filter(({ node }) => node.viewerCanViewAnalytics)
				.map(({ node }) => node.id) ?? [],
		);
	}, [!!query.data, value.length]);

	if (!query.data) {
		return <Spinner isLoading size="18px" />;
	}

	return (
		<Box>
			{_.sortBy(groups, 'name')
				.slice(0, value.length > 5 ? 4 : 5)
				.map((group) => (
					<Box mb={2} key={group.id}>
						<Flex alignItems="center">
							<Box flexGrow={0} flexShrink={0} width={20}>
								<input
									style={{ display: 'block' }}
									type="checkbox"
									checked
									onChange={() => {
										setValue(value.filter((id) => id !== group.id));
									}}
								/>
							</Box>
							<Box>
								<Color color="slateBlue">{group.name}</Color>
							</Box>
						</Flex>
					</Box>
				))}
			{value.length === 0 && (
				<Box mb={2}>
					<Color color="slateBlue">{t('dashboard.sidebar.filters.departments.all')}</Color>
				</Box>
			)}
			{value.length > 5 && (
				<Flex alignItems="center" mb={2}>
					<Box flexGrow={0} flexShrink={0} width={20} />
					<Color color="slateBlue">
						{t('dashboard.sidebar.filters.scope.more', { count: value.length - 4 })}
					</Color>
				</Flex>
			)}
			<Flex alignItems="center">
				{value.length > 0 && <Box flexGrow={0} flexShrink={0} width={20} />}
				<Link onClick={() => setModal(true)}>
					{value.length > 0
						? t('dashboard.sidebar.filters.scope.extendSelection')
						: t('dashboard.sidebar.filters.scope.narrowSelection')}
				</Link>
			</Flex>
			{modal && groups && (
				<DashboardDepartmentsSelectorModal
					groups={query.data.groups.edges.map(({ node }) => node as graphql.Group)}
					selection={groups as graphql.Group[]}
					onChange={(groups) => setValue(groups.map(({ id }) => id))}
					canSelect={(group) =>
						(props.engagement ? group.viewerCanViewEngagementAnalytics : false) ||
						group.viewerCanViewAnalytics
					}
					onDismiss={() => setModal(false)}
				/>
			)}
		</Box>
	);
}
